import React, { FC, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react';
import { message } from 'antd';
import sc_widget from '@/assets/images/widget/bookmark.png';
import sc_widget_mini from '@/assets/images/widget/bookmark_mini.png';
import GridMain from '@/components/GridBoards/GridMain';
import type { TItem, IGridItem } from '@/components/GridBoards/types';
import { $t, $fm } from '@/Intl';
import { useLayoutShortcut } from '@/store';
import { addrValidate, deepClone, makeupUrl } from '@/utils';
import { getArea } from '../doWithSider';

interface ShortcutModalProps {
  onMaskClose: () => void;
  i: number;
  maskWidth: number | undefined;
  maskHeight: number | undefined;
  gm: GridMain; // gridmain 实例
  scid: string;
}

const CurScModal: FC<ShortcutModalProps> = observer(
  ({ onMaskClose, i, maskWidth, maskHeight, gm, scid }) => {
    const [sc = [], updateLayoutSc] = useLayoutShortcut();
    const urlRef = useRef<HTMLInputElement>(null);
    const nameRef = useRef<HTMLInputElement>(null);
    const cloneSc = deepClone(sc);
    const cursc = sc[i];

    const handleMouseDown = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (e.target === e.currentTarget) {
        onMaskClose();
      }
    };

    const handleCancel = () => {
      onMaskClose();
    };

    const handleSave = () => {
      const _url = urlRef.current?.value || '';
      const _name = nameRef.current?.value || '';
      if (!_name.trim()) {
        message.error($t('SC_NAME_ERR_TIP'));
        return false;
      }
      if (!addrValidate(_url)) {
        message.error($t('URL_ERROR_TIP'));
        return false;
      }
      cloneSc[i].url = makeupUrl(_url);
      cloneSc[i].title = _name;
      gm.itemAttrs[scid].url = makeupUrl(_url);
      gm.itemAttrs[scid].title = _name;
      updateLayoutSc(cloneSc);
      onMaskClose();
    };

    return createPortal(
      <div
        className='layout-box-shortcut-details'
        onMouseDown={handleMouseDown}
        style={{ width: maskWidth, height: maskHeight }}
      >
        <div className='sc-title'>
          <label>{$fm('SC_NAME')}</label>
          <input ref={nameRef} defaultValue={cursc?.title} />
        </div>
        <div className='sc-url'>
          <label>{$fm('URL')}</label>
          <input ref={urlRef} defaultValue={cursc?.url} />
        </div>
        <div className='sc-btn'>
          <span className='sc-cancel' onClick={handleCancel}>
            {$fm('CANCEL')}
          </span>
          <span className='sc-save' onClick={handleSave}>
            {$fm('SAVE')}
          </span>
        </div>
      </div>,
      gm.mainRef.current!.parentElement!,
    );
  },
);

interface ShortcutItemProps {
  item: IGridItem;
  screen: number;
  gm: GridMain;
}

const ShortcutItem: FC<ShortcutItemProps> = observer(({ item, screen, gm }) => {
  const [sc = []] = useLayoutShortcut();
  const [opened, setOpened] = useState(false);
  const i = sc.findIndex(
    (s) => s.screen === screen && s.x === item.x && s.y === item.y,
  );
  const cursc = sc[i];
  return (
    <div
      className='layout-widget'
      onDoubleClick={(e) => {
        setOpened(true);
      }}
    >
      <img src={sc_widget} alt='sc' draggable={false} className='img-sc' />
      <span>{cursc?.title || $t('UNNAME_BOOKMARK')}</span>
      {opened ? (
        <CurScModal
          onMaskClose={() => setOpened(false)}
          maskHeight={gm.props.height}
          maskWidth={gm.props.width}
          i={i}
          gm={gm}
          scid={item.id}
        />
      ) : null}
    </div>
  );
});

const createShortcut = (
  props: {
    x?: number;
    y?: number;
    title?: string;
    screen?: number;
    url?: string;
  },
  siderbounds: LayoutBoxSiderbounds,
): TItem => {
  const _title = props.title || $t('UNNAME_BOOKMARK');
  const _url = props.url ?? '';
  const _x = props.x ?? -1;
  const _y = props.y ?? -1;
  return {
    id: 'shortcut',
    x: _x,
    y: _y,
    w: 1,
    h: 1,
    url: _url,
    title: _title,
    isWidget: true,
    repeat: true,
    area: getArea(siderbounds),
    render: (item, __, ___, gm) => {
      return <ShortcutItem item={item} screen={props.screen || 0} gm={gm} />;
    },
    renderPick: () => {
      return (
        <div className='layout-pick-widget'>
          <img
            src={sc_widget_mini}
            alt='sc'
            draggable={false}
            className='img-sc'
          />
          <span>
            {$fm('BOOKMARK')}
            <br />( 1 × 1 )
          </span>
        </div>
      );
    },
  };
};

export default createShortcut;
